import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Bug, Check, LifeBuoy, Lightbulb, Paperclip, Send, X } from 'lucide-react';
import {
  SUPPORT_AREAS,
  SUPPORT_AREA_LABELS,
  SUPPORT_ATTACHMENT_MAX_BYTES,
  SUPPORT_PRIORITIES,
  SUPPORT_PRIORITY_LABELS,
  type SupportArea,
  type SupportPriority,
  type SupportTicketDetail,
  type SupportTicketKind,
} from '@asyagrup/group-hub-contracts';
import { errorMessage } from '@/lib/api/client';
import {
  Button,
  Checkbox,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
  cx,
} from '@/ui/primitives';
import { CompanyField, useCompanyChoice } from '@/ui/CompanyField';
import { supportKeys, useCreateTicket, useUploadAttachment } from './api';
import { areaFromPath, formatBytes } from './support-ui';

const DRAFT_KEY = 'gh_support_draft:new';

const TEMPLATE = `Ne yapıyordum:
Ne olmasını bekliyordum:
Bunun yerine ne oldu:
Hata mesajı (varsa):`;

interface FormState {
  kind: SupportTicketKind;
  title: string;
  description: string;
  pageUrl: string;
  area: string;
  priority: SupportPriority;
  blocking: boolean;
}

function initialState(
  kind: SupportTicketKind | null,
  prefillTitle: string,
  pageUrl?: string,
): FormState {
  /**
   * `pageUrl` is recorded as `window.location.href` today — but you had to LEAVE
   * the broken screen to reach Destek, so the field meant to remove the question
   * "hangi ekranda?" reliably records `/destek` instead. The same-origin
   * referrer is the screen the user actually came from.
   */
  let referrer = pageUrl ?? '';
  if (!referrer) {
    try {
      if (document.referrer && new URL(document.referrer).origin === window.location.origin)
        referrer = new URL(document.referrer).pathname + new URL(document.referrer).search;
    } catch {
      referrer = '';
    }
  }
  return {
    kind: kind ?? 'BUG',
    title: prefillTitle,
    description: '',
    pageUrl: referrer,
    area: areaFromPath(referrer),
    priority: 'NORMAL',
    blocking: false,
  };
}

function readDraft(): Partial<FormState> | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Partial<FormState>) : null;
  } catch {
    return null;
  }
}

/**
 * The compose flow — and a receipt that does not close on submit.
 *
 * `useCreateTicket` returns a full `SupportTicketDetail` and today's
 * `onSuccess: onClose` throws it away: the sheet closes, with no confirmation,
 * no number, and — if the current filters exclude it — not even a row in the
 * list. The user is left with no evidence at all that the thing they reported
 * exists.
 */
export function CreateTicketDialog({
  initialKind,
  prefillTitle = '',
  /**
   * The screen the reporter is actually on, when the dialog was opened from it.
   * Absent from Destek's records half (`destekKayitEkraninda`), where the
   * referrer guess above is still the best available answer; present from every
   * other screen, Destek's Yenilikler tab included.
   */
  initialPageUrl,
  /** A screenshot taken before this sheet covered the thing being reported. */
  initialFiles,
  canManage,
  onClose,
  onOpenDetail,
  onAnnounce,
  onNotice,
}: {
  initialKind: SupportTicketKind | null;
  prefillTitle?: string;
  initialPageUrl?: string;
  initialFiles?: File[];
  canManage: boolean;
  onClose: () => void;
  onOpenDetail: (id: string) => void;
  onAnnounce: (message: string) => void;
  /** The trace left on the page once the receipt closes — proof it exists. */
  onNotice: (notice: { text: string; ticketId: string }) => void;
}) {
  const create = useCreateTicket();
  const queryClient = useQueryClient();
  const company = useCompanyChoice();

  const [form, setForm] = useState<FormState>(() => {
    const base = initialState(initialKind, prefillTitle, initialPageUrl);
    const draft = readDraft();
    return draft && !prefillTitle ? { ...base, ...draft, kind: initialKind ?? base.kind } : base;
  });
  const [restored] = useState(() => (prefillTitle ? false : readDraft() !== null));
  const [files, setFiles] = useState<File[]>(() => initialFiles ?? []);
  const [fileError, setFileError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | undefined>();
  const [descError, setDescError] = useState<string | undefined>();
  const [areaError, setAreaError] = useState<string | undefined>();
  const [dragging, setDragging] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const [phase, setPhase] = useState<'form' | 'sending' | 'done'>('form');
  const [created, setCreated] = useState<SupportTicketDetail | null>(null);
  const [uploaded, setUploaded] = useState(0);

  const upload = useUploadAttachment(created?.id ?? '');
  const uploadRef = useRef(upload);
  uploadRef.current = upload;
  const filesRef = useRef(files);
  filesRef.current = files;

  const dirty =
    form.title.trim().length > 0 || form.description.trim().length > 0 || files.length > 0;

  useEffect(() => {
    if (phase !== 'form') return;
    try {
      if (dirty) sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      else sessionStorage.removeItem(DRAFT_KEY);
    } catch {
      // Losing the draft is bad; crashing the form is worse.
    }
  }, [form, dirty, phase]);

  // Attachments upload sequentially once the ticket exists: the upload endpoint
  // needs a ticket id and that only arrives with the response. A failed file
  // does not stop the queue — the ticket is already open, and the receipt
  // counter says what actually landed.
  useEffect(() => {
    if (phase !== 'sending' || !created) return;
    let cancelled = false;
    void (async () => {
      for (const file of filesRef.current) {
        if (cancelled) return;
        try {
          await uploadRef.current.mutateAsync({ file });
        } catch {
          // Swallowed: the receipt counter is what makes a missing file visible.
        }
        if (!cancelled) setUploaded((n) => n + 1);
      }
      if (!cancelled) setPhase('done');
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, created]);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const accepted: File[] = [];
    for (const file of Array.from(incoming)) {
      if (file.size > SUPPORT_ATTACHMENT_MAX_BYTES) {
        setFileError(
          `Dosya 10 MB sınırını aşıyor (${formatBytes(file.size)}). Küçültüp tekrar deneyin.`,
        );
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length > 0) {
      setFileError(null);
      setFiles((prev) => [...prev, ...accepted]);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    // Validation in React and in Turkish: the browser's own bubble speaks the
    // browser's language, and `Field`'s `error` prop is unused today.
    const title = form.title.trim();
    const description = form.description.trim();
    const nextTitleError = title.length < 5 ? 'Başlık en az 5 karakter olmalı.' : undefined;
    const nextDescError =
      description.length < 10 ? 'Biraz daha anlatın — en az 10 karakter.' : undefined;
    const nextAreaError = form.area ? undefined : 'Kaydın hangi ekranla ilgili olduğunu seçin.';
    setTitleError(nextTitleError);
    setDescError(nextDescError);
    setAreaError(nextAreaError);
    if (nextTitleError || nextDescError || nextAreaError) return;
    // `CompanyField` marks itself `required`, so the browser already blocks
    // submit; this is only a second gate.
    if (company.required && !company.companyId) return;

    create.mutate(
      {
        kind: form.kind,
        title,
        description,
        area: form.area as SupportArea,
        priority: canManage ? form.priority : form.blocking ? 'HIGH' : 'NORMAL',
        ...(form.pageUrl.trim() ? { pageUrl: form.pageUrl.trim().slice(0, 500) } : {}),
        ...(company.companyId ? { companyId: company.companyId } : {}),
      },
      {
        onSuccess: (ticket) => {
          // Prime the cache from the response we already hold, so "Kaydı aç"
          // opens with no round trip and no spinner.
          queryClient.setQueryData(supportKeys.detail(ticket.id), ticket);
          setCreated(ticket);
          try {
            sessionStorage.removeItem(DRAFT_KEY);
          } catch {
            // yoksay
          }
          onAnnounce(`${ticket.number} numaralı bildiriminiz alındı.`);
          onNotice({
            text: `${ticket.number} numaralı kaydınız oluşturuldu.`,
            ticketId: ticket.id,
          });
          setPhase('sending');
        },
      },
    );
  };

  const requestClose = () => {
    if (phase === 'form' && dirty) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  const title =
    phase === 'done'
      ? 'Bildiriminiz alındı'
      : form.kind === 'BUG'
        ? 'Hata bildir'
        : 'Geliştirme öner';

  if (phase !== 'form' && created) {
    return (
      <Modal open title={title} onClose={onClose}>
        <div className="py-6 text-center">
          <span className="bg-success/15 text-success mx-auto flex size-12 items-center justify-center rounded-full">
            <Check size={24} aria-hidden />
          </span>
          <p className="text-fg mt-3 text-base font-semibold">Bildiriminiz alındı</p>
          <p className="text-fg-muted mt-1 text-sm">
            Kayıt numaranız <span className="text-fg font-mono">{created.number}</span>. Destek
            ekibi baktığında ve durum değiştiğinde bildirim alacaksınız.
          </p>
          {phase === 'sending' && files.length > 0 && (
            <Spinner label={`Ek dosyalar yükleniyor (${uploaded}/${files.length})`} />
          )}
          <div className="mt-4 grid grid-cols-1 gap-2 md:flex md:flex-wrap md:justify-center">
            {/* Ekler yüklenirken «Kaydı aç» BEKLİYOR. Kaydı açmak bu pencereyi
                kapatıyor (`openDetail` `yeni`yi siliyor) ve kapanan pencere
                yükleme döngüsünü yarıda kesiyor: ilk dosyadan sonrakiler
                sessizce hiç gitmezdi, sayacı söyleyen pencere de kalmazdı. */}
            <Button
              variant="primary"
              disabled={phase === 'sending'}
              onClick={() => {
                onClose();
                onOpenDetail(created.id);
              }}
            >
              Kaydı aç
            </Button>
            <Button onClick={onClose}>Kapat</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      title={title}
      onClose={requestClose}
      footer={
        <div className="flex flex-col-reverse gap-2 md:flex-row md:justify-end">
          <Button type="button" block onClick={requestClose}>
            Vazgeç
          </Button>
          <Button type="submit" form="dst-create" variant="primary" block busy={create.isPending}>
            <Send size={15} aria-hidden /> Gönder
          </Button>
        </div>
      }
    >
      {confirmClose && (
        <div className="border-warning/30 bg-warning/10 mb-3 flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2 text-sm">
          <span className="text-fg min-w-0 flex-1">
            Yazdıklarınız kaybolacak. Yine de kapatılsın mı?
          </span>
          <Button size="sm" variant="danger" onClick={onClose}>
            Kapat
          </Button>
          <Button size="sm" onClick={() => setConfirmClose(false)}>
            Vazgeç
          </Button>
        </div>
      )}
      {restored && (
        <p className="text-fg-muted mb-3 text-xs">Yarım kalan taslağınız geri yüklendi.</p>
      )}

      <form id="dst-create" className="space-y-4" onSubmit={submit}>
        {initialKind === null ? (
          <div className="flex gap-2">
            {(['BUG', 'FEATURE'] as const).map((k) => (
              <label
                key={k}
                className={cx(
                  'flex min-h-11 flex-1 cursor-pointer touch-manipulation items-center gap-2 rounded-lg border px-3 text-sm',
                  form.kind === k
                    ? 'border-primary bg-primary/10 text-fg'
                    : 'border-border text-fg-muted hover:bg-surface-muted',
                )}
              >
                <input
                  type="radio"
                  name="dst-kind"
                  className="size-5 shrink-0 md:size-4"
                  checked={form.kind === k}
                  onChange={() => setForm((f) => ({ ...f, kind: k }))}
                />
                {k === 'BUG' ? <Bug size={16} aria-hidden /> : <Lightbulb size={16} aria-hidden />}
                {k === 'BUG' ? 'Bir şey bozuk' : 'Bir şey olsun istiyorum'}
              </label>
            ))}
          </div>
        ) : (
          <button
            type="button"
            className="text-fg-muted hover:text-fg text-xs underline"
            onClick={() => setForm((f) => ({ ...f, kind: f.kind === 'BUG' ? 'FEATURE' : 'BUG' }))}
          >
            {form.kind === 'BUG' ? 'Aslında bu bir geliştirme talebi' : 'Aslında bu bir hata'}
          </button>
        )}

        <p className="text-fg-muted text-sm">
          Kısaca ne olduğunu yazın. Eksik kalırsa destek ekibi buradan size sorar.
        </p>

        <Field
          label="Ne oldu?"
          htmlFor="dst-title"
          hint='Tek cümle yeter. Örn. "Ödeme ekranında Kaydet düğmesi hata veriyor."'
          error={titleError}
        >
          <Input
            id="dst-title"
            value={form.title}
            maxLength={200}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
          {form.title.length > 160 && (
            <p className="text-fg-muted mt-1 text-xs">{form.title.length}/200</p>
          )}
        </Field>

        <Field
          label="Biraz daha anlatın"
          htmlFor="dst-desc"
          hint="Ne kadar somut yazarsanız o kadar hızlı çözülür."
          error={descError}
        >
          <Textarea
            id="dst-desc"
            rows={7}
            value={form.description}
            maxLength={5000}
            placeholder={TEMPLATE}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {/* The DTO argues the ten-character floor exists for report
                quality; this is that argument turned into UI instead of a hint
                nobody reads. */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setForm((f) => ({ ...f, description: f.description || TEMPLATE }))}
            >
              Şablonu doldur
            </Button>
            {form.description.length > 4000 && (
              <span className="text-fg-muted text-xs">{form.description.length}/5000</span>
            )}
          </div>
        </Field>

        <Field
          label="Sorunu gördüğünüz ekran"
          htmlFor="dst-url"
          hint="Bulunduğunuz ekrandan tahmin edildi; yanlışsa düzeltin."
        >
          <Input
            id="dst-url"
            value={form.pageUrl}
            maxLength={500}
            placeholder="/odemeler/hafta/2026-33"
            onChange={(e) => {
              const pageUrl = e.target.value;
              setForm((f) => ({
                ...f,
                pageUrl,
                area: f.area || areaFromPath(pageUrl),
              }));
            }}
          />
        </Field>

        <Field
          label="İlgili ekran"
          htmlFor="dst-area"
          hint="Doğru ekran, kaydı doğru kişiye götürür."
          error={areaError}
        >
          {/* No `OTHER` default: preselecting the least useful value is why most
              tickets land in "Diğer" and the field's stated routing purpose is
              defeated on arrival. */}
          <Select
            id="dst-area"
            required
            value={form.area}
            onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))}
          >
            <option value="">Seçin</option>
            {SUPPORT_AREAS.map((a) => (
              <option key={a} value={a}>
                {SUPPORT_AREA_LABELS[a]}
              </option>
            ))}
          </Select>
        </Field>

        {canManage ? (
          <Field label="Önem" htmlFor="dst-priority" hint="Sıralamayı destek ekibi belirler.">
            <Select
              id="dst-priority"
              value={form.priority}
              onChange={(e) =>
                setForm((f) => ({ ...f, priority: e.target.value as SupportPriority }))
              }
            >
              {SUPPORT_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {SUPPORT_PRIORITY_LABELS[p]}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field
            label="Önem"
            hint="Sıralamayı destek ekibi belirler; bu bilgi onlara yardımcı olur."
          >
            {/* One plain-language bit for the reporter rather than a four-value
                dial: LOW and CRITICAL stay reachable through triage. */}
            <Checkbox
              label="İşimi tamamen durduruyor"
              checked={form.blocking}
              onChange={(e) => setForm((f) => ({ ...f, blocking: e.target.checked }))}
            />
          </Field>
        )}

        <CompanyField
          choice={company}
          idPrefix="dst"
          hint="Kayıt bu şirketin destek kuyruğuna düşer."
        />

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            addFiles(e.dataTransfer.files);
          }}
          onPaste={(e) => addFiles(e.clipboardData.files)}
          className={cx(
            'rounded-xl border border-dashed p-3 text-center transition',
            dragging ? 'border-primary bg-primary/5' : 'border-border bg-surface-muted/40',
          )}
        >
          <label className="text-fg inline-flex min-h-11 cursor-pointer touch-manipulation items-center gap-2 text-sm">
            <Paperclip size={15} aria-hidden /> Ekran görüntüsü ekleyin
            <input
              type="file"
              multiple
              accept="image/png,image/jpeg,application/pdf"
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </label>
          <p className="text-fg-muted mt-1 text-xs">
            Sürükleyip bırakabilir ya da Ctrl+V ile yapıştırabilirsiniz. Dosya başına en fazla 10
            MB.
          </p>
          {/* DST-00078: buradan açılan bir bildirimin görüntüsü destek listesi
              olurdu. Görüntünün işe yarayanı sorunun yaşandığı ekrandakidir, ve
              bunu söyleyen başka hiçbir yer yoktu.
              `initialPageUrl` varsa pencereyi üst çubuktaki düğme bir EKRANDAN
              açtı — Destek'in Yenilikler sekmesi dahil — ve okuyucu ipucunun
              söylediğini zaten yaptı; görüntü alınamadıysa ya da silindiyse
              bile «o düğmeye basın» demek ona yanlış yolu gösterirdi. */}
          {files.length === 0 &&
            !initialPageUrl &&
            window.location.pathname.startsWith('/destek') && (
              <p className="text-fg-muted mt-2 text-[13px]">
                İpucu: sorunu yaşadığınız ekrana dönüp üst çubuktaki{' '}
                <LifeBuoy size={13} className="inline align-[-2px]" aria-label="Sorun bildir" />{' '}
                düğmesine basarsanız o ekranın görüntüsü ve adresi kendiliğinden eklenir.
              </p>
            )}
        </div>

        {files.length > 0 && (
          <ul className="border-border divide-border divide-y rounded-xl border">
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`} className="flex items-center gap-2 px-3 py-2">
                <span className="text-fg min-w-0 flex-1 text-sm break-all">{file.name}</span>
                <span className="text-fg-muted shrink-0 text-xs">{formatBytes(file.size)}</span>
                <button
                  type="button"
                  aria-label={`${file.name} ekini kaldır`}
                  className="text-fg-muted hover:text-fg shrink-0"
                  onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                >
                  <X size={15} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}

        {fileError && <ErrorState message={fileError} />}
        {create.isError && <ErrorState message={errorMessage(create.error)} />}
      </form>
    </Modal>
  );
}
